import React, { useState, useEffect, useCallback } from 'react';
import axios from './axiosSetup';
import API_BASE from './apiBase';
import CommonPagination from './CommonPagination';
import { formatCompactNumber } from './numberFormatUtils';
import maskUtils from './maskUtils';
import '../css/market-consent.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faSearch,
  faSyncAlt,
  faDownload,
  faCode,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faTimes,
  faCopy,
  faCheck,
  faExternalLinkAlt,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons';

import Swal from 'sweetalert2';

const { maskEmail, maskPhone } = maskUtils;

function MarketConsentPage({ user }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, granted: 0, declined: 0, today: 0 });

  // Filters and Pagination State
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [consentFilter, setConsentFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modals
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        sort_by: 'created_at',
        sort_order: 'desc',
      };
      if (search.trim()) params.search = search.trim();
      if (consentFilter && consentFilter !== 'all') params.consent = consentFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (user?.tenant_id) params.tenant_id = user.tenant_id;

      const response = await axios.get(`${API_BASE}/api/market-consent`, { params });
      if (response.data && response.data.success) {
        setRecords(response.data.items || []);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      }
    } catch (err) {
      console.error('Failed to load market consent records:', err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, consentFilter, startDate, endDate, user?.tenant_id]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (search.trim()) params.append('search', search.trim());
    if (consentFilter && consentFilter !== 'all') params.append('consent', consentFilter);
    if (user?.tenant_id) params.append('tenant_id', String(user.tenant_id));

    const exportUrl = `${API_BASE}/api/market-consent/export/csv?${params.toString()}`;
    window.open(exportUrl, '_blank');
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedSnippet(key);
    setTimeout(() => setCopiedSnippet(''), 2500);
  };

  const currentApiBase = typeof window !== 'undefined' ? window.location.origin : 'https://consent.whitehats.tech';
  const apiEndpointUrl = `${currentApiBase}/api/market-consent`;

  const sampleJsonPayload = JSON.stringify(
    {
      id: "EXT-10492",
      name: "John Doe",
      phone: "+1 555-019-2834",
      email: "john.doe@example.com",
      promotion: "Black Friday 2026 Special",
      message: "Customer consented to receive marketing emails and product updates.",
      consent: "accepted"
    },
    null,
    2
  );

  const sampleCurl = `curl -X POST "${apiEndpointUrl}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY_HERE" \\
  -d '${sampleJsonPayload.replace(/\n/g, '\n  ')}'`;

  const sampleJs = `fetch("${apiEndpointUrl}", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": "YOUR_API_KEY_HERE"
  },
  body: JSON.stringify(${sampleJsonPayload.replace(/\n/g, '\n    ')})
})
.then(res => res.json())
.then(data => console.log("Success:", data))
.catch(err => console.error("Error:", err));`;

  return (
    <div className="market-consent-container">
      {/* ── Page Header ── */}
      <div className="mc-header">
        <div className="mc-title-group">
          <h1>
            <FontAwesomeIcon icon={faBullhorn} style={{ color: '#2563eb' }} />
            Market Consent Records
            <span className="mc-badge">{totalItems} Total</span>
          </h1>
          <p className="mc-subtitle">
            All consent records submitted from external websites and marketing channels via API. Real-time logging, validation, and analytics.
          </p>
        </div>
        <div className="mc-actions">
          <button
            type="button"
            className="mc-btn mc-btn-api"
            onClick={() => setShowGuideModal(true)}
            title="View integration guide & API documentation"
          >
            <FontAwesomeIcon icon={faCode} />
            API Guide
          </button>
          <button
            type="button"
            className="mc-btn mc-btn-outline"
            onClick={handleExportCSV}
            title="Download records as CSV"
          >
            <FontAwesomeIcon icon={faDownload} />
            Export CSV
          </button>
          <button
            type="button"
            className="mc-btn mc-btn-primary"
            onClick={fetchRecords}
            disabled={loading}
            title="Refresh records"
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── KPI Summary Cards ── */}
      <div className="mc-stats-grid">
        <div className="mc-stat-card">
          <div className="mc-stat-info">
            <div className="mc-stat-label">Total Submissions</div>
            <div className="mc-stat-value" title={Number(stats.total || 0).toLocaleString()}>
              {formatCompactNumber(stats.total || 0)}
            </div>
          </div>
          <div className="mc-stat-icon-wrapper mc-stat-blue">
            <FontAwesomeIcon icon={faBullhorn} />
          </div>
        </div>

        <div className="mc-stat-card">
          <div className="mc-stat-info">
            <div className="mc-stat-label">Granted / Accepted</div>
            <div className="mc-stat-value" style={{ color: '#16a34a' }} title={Number(stats.granted || 0).toLocaleString()}>
              {formatCompactNumber(stats.granted || 0)}
            </div>
          </div>
          <div className="mc-stat-icon-wrapper mc-stat-green">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
        </div>

        <div className="mc-stat-card">
          <div className="mc-stat-info">
            <div className="mc-stat-label">Declined / Opted Out</div>
            <div className="mc-stat-value" style={{ color: '#dc2626' }} title={Number(stats.declined || 0).toLocaleString()}>
              {formatCompactNumber(stats.declined || 0)}
            </div>
          </div>
          <div className="mc-stat-icon-wrapper mc-stat-red">
            <FontAwesomeIcon icon={faTimesCircle} />
          </div>
        </div>

        <div className="mc-stat-card">
          <div className="mc-stat-info">
            <div className="mc-stat-label">Received Today</div>
            <div className="mc-stat-value" title={Number(stats.today || 0).toLocaleString()}>
              {formatCompactNumber(stats.today || 0)}
            </div>
          </div>
          <div className="mc-stat-icon-wrapper mc-stat-purple">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
        </div>
      </div>

      {/* ── Filter Controls ── */}
      <div className="mc-filter-card">
        <div className="mc-filter-row">
          <div className="mc-search-box">
            <FontAwesomeIcon icon={faSearch} className="mc-search-icon" />
            <input
              type="text"
              className="mc-search-input"
              placeholder="Search by name, email, phone, promotion, message..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="mc-status-tabs">
            <button
              type="button"
              className={`mc-tab-btn ${consentFilter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setConsentFilter('all');
                setPage(1);
              }}
            >
              All Status
            </button>
            <button
              type="button"
              className={`mc-tab-btn ${consentFilter === 'granted' ? 'active' : ''}`}
              onClick={() => {
                setConsentFilter('granted');
                setPage(1);
              }}
            >
              Granted
            </button>
            <button
              type="button"
              className={`mc-tab-btn ${consentFilter === 'declined' ? 'active' : ''}`}
              onClick={() => {
                setConsentFilter('declined');
                setPage(1);
              }}
            >
              Declined
            </button>
          </div>

          <div className="mc-date-group">
            <input
              type="date"
              className="mc-date-input"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              title="From Date"
            />
            <span style={{ color: '#94a3b8' }}>to</span>
            <input
              type="date"
              className="mc-date-input"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              title="To Date"
            />
            {(search || consentFilter !== 'all' || startDate || endDate) && (
              <button
                type="button"
                className="mc-btn mc-btn-outline"
                style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                onClick={() => {
                  setSearch('');
                  setConsentFilter('all');
                  setStartDate('');
                  setEndDate('');
                  setPage(1);
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Data Table ── */}
      <div className="mc-table-container">
        <div className="mc-table-responsive">
          <table className="mc-table">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>ID</th>
                <th>Customer Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Promotion</th>
                <th>Message</th>
                <th>Consent Status</th>
                <th>Received At (UTC)</th>
                <th style={{ textAlign: 'center', width: '80px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan="9">
                    <div className="mc-empty-state">
                      <div className="mc-empty-icon">
                        <FontAwesomeIcon icon={faBullhorn} />
                      </div>
                      <h3>No Market Consent Records Found</h3>
                      <p>
                        {search || consentFilter !== 'all'
                          ? 'No records matched your search filters. Try adjusting your query.'
                          : 'Connect an external website using our API endpoint to begin collecting and streaming consent data.'}
                      </p>
                      <button
                        type="button"
                        className="mc-btn mc-btn-api"
                        onClick={() => setShowGuideModal(true)}
                      >
                        <FontAwesomeIcon icon={faCode} /> View API Integration Guide
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((r) => {
                  const isGranted = ['granted', 'accepted', 'yes', 'true'].includes(
                    String(r.consent || '').toLowerCase()
                  );
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600, color: '#64748b' }}>#{r.id}</td>
                      <td style={{ fontWeight: 600, color: '#0f172a' }}>{r.name || '—'}</td>
                      <td>
                        <span style={{ color: '#2563eb' }}>
                          {maskEmail(r.email) || '—'}
                        </span>
                      </td>
                      <td>{maskPhone(r.phone) || '—'}</td>
                      <td>
                        <span style={{ fontSize: '0.82rem', color: '#475569' }}>
                          {r.promotion ? (r.promotion.length > 30 ? r.promotion.substring(0, 30) + '...' : r.promotion) : '—'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                          {r.message ? (r.message.length > 35 ? r.message.substring(0, 35) + '...' : r.message) : '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`mc-status-pill ${isGranted ? 'mc-status-granted' : 'mc-status-declined'}`}>
                          <FontAwesomeIcon icon={isGranted ? faCheckCircle : faTimesCircle} />
                          {r.consent || 'granted'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.82rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="mc-btn mc-btn-outline"
                          style={{ padding: '5px 9px', fontSize: '0.78rem' }}
                          onClick={() => setSelectedRecord(r)}
                          title="View complete record details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <CommonPagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={perPage}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(size) => {
              setPerPage(size);
              setPage(1);
            }}
            loading={loading}
          />
        )}
      </div>

      {/* ── Record Details Modal ── */}
      {selectedRecord && (
        <div className="mc-modal-backdrop" onClick={() => setSelectedRecord(null)}>
          <div className="mc-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <h2>
                <FontAwesomeIcon icon={faBullhorn} style={{ color: '#2563eb' }} />
                Market Consent Details — #{selectedRecord.id}
              </h2>
              <button
                type="button"
                className="mc-modal-close"
                onClick={() => setSelectedRecord(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="mc-modal-body">
              <div className="mc-detail-grid">
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Customer Name</div>
                  <div className="mc-detail-value">{selectedRecord.name || '—'}</div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Email Address</div>
                  <div className="mc-detail-value">{maskEmail(selectedRecord.email) || '—'}</div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Phone Number</div>
                  <div className="mc-detail-value">{maskPhone(selectedRecord.phone) || '—'}</div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Consent Status</div>
                  <div className="mc-detail-value">
                    <span
                      className={`mc-status-pill ${['granted', 'accepted', 'yes', 'true'].includes(
                        String(selectedRecord.consent || '').toLowerCase()
                      )
                          ? 'mc-status-granted'
                          : 'mc-status-declined'
                        }`}
                    >
                      {selectedRecord.consent || 'granted'}
                    </span>
                  </div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">External / Client ID</div>
                  <div className="mc-detail-value">{selectedRecord.external_id || 'None provided'}</div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Source Client IP</div>
                  <div className="mc-detail-value">{selectedRecord.source_ip || '127.0.0.1'}</div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Created At</div>
                  <div className="mc-detail-value">
                    {selectedRecord.created_at ? new Date(selectedRecord.created_at).toUTCString() : '—'}
                  </div>
                </div>
                <div className="mc-detail-item">
                  <div className="mc-detail-label">Source Website</div>
                  <div className="mc-detail-value">{selectedRecord.source_website || 'Direct API Call'}</div>
                </div>
              </div>

              {selectedRecord.promotion && (
                <div style={{ marginBottom: '16px' }}>
                  <div className="mc-detail-label">Promotion Info</div>
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '0.88rem',
                    }}
                  >
                    {selectedRecord.promotion}
                  </div>
                </div>
              )}

              {selectedRecord.message && (
                <div style={{ marginBottom: '16px' }}>
                  <div className="mc-detail-label">Customer Message / Note</div>
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '10px 14px',
                      fontSize: '0.88rem',
                    }}
                  >
                    {selectedRecord.message}
                  </div>
                </div>
              )}

              {selectedRecord.raw_payload && (
                <div>
                  <div className="mc-detail-label">Raw Submitted JSON Payload</div>
                  <div className="mc-code-block" style={{ margin: '6px 0 0 0' }}>
                    <button
                      type="button"
                      className="mc-code-copy-btn"
                      onClick={() => copyToClipboard(selectedRecord.raw_payload, 'raw')}
                    >
                      <FontAwesomeIcon icon={copiedSnippet === 'raw' ? faCheck : faCopy} />{' '}
                      {copiedSnippet === 'raw' ? 'Copied' : 'Copy'}
                    </button>
                    <pre style={{ margin: 0 }}>{selectedRecord.raw_payload}</pre>
                  </div>
                </div>
              )}
            </div>
            <div className="mc-modal-footer">
              <button
                type="button"
                className="mc-btn mc-btn-outline"
                onClick={() => setSelectedRecord(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── API Integration Guide Modal ── */}
      {showGuideModal && (
        <div className="mc-modal-backdrop" onClick={() => setShowGuideModal(false)}>
          <div className="mc-modal-card" style={{ maxWidth: '800px' }} onClick={(e) => e.stopPropagation()}>
            <div className="mc-modal-header">
              <h2>
                <FontAwesomeIcon icon={faCode} style={{ color: '#2563eb' }} />
                External Website Integration Guide
              </h2>
              <button
                type="button"
                className="mc-modal-close"
                onClick={() => setShowGuideModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="mc-modal-body">
              <div className="mc-doc-section">
                <h4>1. Endpoint Overview</h4>
                <p>
                  External websites or mobile apps can submit customer consent records by sending an HTTP POST request to:
                </p>
                <div className="mc-code-block">
                  <button
                    type="button"
                    className="mc-code-copy-btn"
                    onClick={() => copyToClipboard(apiEndpointUrl, 'url')}
                  >
                    <FontAwesomeIcon icon={copiedSnippet === 'url' ? faCheck : faCopy} />{' '}
                    {copiedSnippet === 'url' ? 'Copied' : 'Copy'}
                  </button>
                  <pre style={{ margin: 0 }}>POST {apiEndpointUrl}</pre>
                </div>
              </div>

              <div className="mc-doc-section">
                <h4>2. Authentication</h4>
                <p>
                  Requests must include an API Key header. You can pass it via:
                </p>
                <ul>
                  <li><code>X-API-Key: YOUR_API_KEY</code> (Recommended)</li>
                  <li><code>Authorization: Bearer YOUR_API_KEY</code></li>
                </ul>
              </div>

              <div className="mc-doc-section">
                <h4>3. Supported Fields & Schema</h4>
                <table className="mc-table" style={{ fontSize: '0.82rem', marginBottom: '14px' }}>
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Type</th>
                      <th>Required</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>name</code></td>
                      <td>String</td>
                      <td>Yes</td>
                      <td>Customer full name (min 2 chars).</td>
                    </tr>
                    <tr>
                      <td><code>email</code></td>
                      <td>String</td>
                      <td>Yes</td>
                      <td>Customer valid email address.</td>
                    </tr>
                    <tr>
                      <td><code>consent</code></td>
                      <td>String / Boolean</td>
                      <td>Yes</td>
                      <td>Consent value (e.g. <code>accepted</code>, <code>granted</code>, <code>true</code>, <code>declined</code>).</td>
                    </tr>
                    <tr>
                      <td><code>phone</code></td>
                      <td>String</td>
                      <td>No</td>
                      <td>Customer phone number with country code.</td>
                    </tr>
                    <tr>
                      <td><code>promotion</code></td>
                      <td>String</td>
                      <td>No</td>
                      <td>Campaign / promotion name or code.</td>
                    </tr>
                    <tr>
                      <td><code>message</code></td>
                      <td>String</td>
                      <td>No</td>
                      <td>Optional customer message or disclosure text.</td>
                    </tr>
                    <tr>
                      <td><code>id</code></td>
                      <td>String / Int</td>
                      <td>No</td>
                      <td>Your external website's internal record ID for cross-reference.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mc-doc-section">
                <h4>4. Example cURL Request</h4>
                <div className="mc-code-block">
                  <button
                    type="button"
                    className="mc-code-copy-btn"
                    onClick={() => copyToClipboard(sampleCurl, 'curl')}
                  >
                    <FontAwesomeIcon icon={copiedSnippet === 'curl' ? faCheck : faCopy} />{' '}
                    {copiedSnippet === 'curl' ? 'Copied' : 'Copy'}
                  </button>
                  <pre style={{ margin: 0 }}>{sampleCurl}</pre>
                </div>
              </div>

              <div className="mc-doc-section">
                <h4>5. Example JavaScript (Fetch)</h4>
                <div className="mc-code-block">
                  <button
                    type="button"
                    className="mc-code-copy-btn"
                    onClick={() => copyToClipboard(sampleJs, 'js')}
                  >
                    <FontAwesomeIcon icon={copiedSnippet === 'js' ? faCheck : faCopy} />{' '}
                    {copiedSnippet === 'js' ? 'Copied' : 'Copy'}
                  </button>
                  <pre style={{ margin: 0 }}>{sampleJs}</pre>
                </div>
              </div>

              <div className="mc-doc-section">
                <h4>6. HTTP Response Status Codes</h4>
                <ul>
                  <li><strong>201 Created</strong>: Submission accepted and stored in <code>market_consent</code> table.</li>
                  <li><strong>400 Bad Request</strong>: Input validation failure (e.g., invalid email or phone format, missing required field).</li>
                  <li><strong>401 Unauthorized</strong>: Missing or invalid API key.</li>
                  <li><strong>500 Internal Server Error</strong>: Database or server issue.</li>
                </ul>
              </div>
            </div>
            <div className="mc-modal-footer">
              <button
                type="button"
                className="mc-btn mc-btn-primary"
                onClick={() => setShowGuideModal(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketConsentPage;
